import axios from "axios";
import { API_BASE_URL } from "../app_url";

/**
 * Per-doctor AI token-usage reporting.
 *
 * After each OpenAI call (recorder transcription, recorder analysis, cobot
 * ChatGPT) the browser POSTs the `usage` counters to the backend, which stores
 * one row per call keyed to the authenticated doctor. This is best-effort
 * telemetry: it must never block or break the AI flow, so every failure is
 * swallowed.
 */

const getAuthHeader = () => {
  try {
    const session = JSON.parse(localStorage.getItem("doctolikLoginInfo") || "null");
    const token = session?.authorisation?.token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
};

const toInt = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
};

/**
 * Normalize the different OpenAI usage shapes to a single form:
 *  - chat/completions: { prompt_tokens, completion_tokens, total_tokens }
 *  - transcriptions / responses: { input_tokens, output_tokens, total_tokens }
 * A missing/undefined usage object yields zeros — the call is still worth logging.
 */
export function normalizeUsage(usage) {
  if (!usage || typeof usage !== "object") {
    return { promptTokens: 0, completionTokens: 0, totalTokens: 0 };
  }
  const promptTokens = toInt(usage.prompt_tokens ?? usage.input_tokens);
  const completionTokens = toInt(usage.completion_tokens ?? usage.output_tokens);
  const totalTokens = toInt(usage.total_tokens) || promptTokens + completionTokens;
  return { promptTokens, completionTokens, totalTokens };
}

/**
 * @param {object} p
 * @param {"recorder_transcription"|"recorder_analysis"|"cobot"|"recorder_session"} p.feature
 * @param {string} p.model
 * @param {number} [p.promptTokens]
 * @param {number} [p.completionTokens]
 * @param {number} [p.totalTokens]
 * @param {number} [p.durationSeconds]  wall-clock length of a "recorder_session"
 * @param {number|string|null} [p.appointmentId]
 * @returns {Promise<void>} always resolves
 */
export function reportAiUsage({
  feature,
  model,
  promptTokens = 0,
  completionTokens = 0,
  totalTokens = 0,
  durationSeconds = 0,
  appointmentId = null,
}) {
  if (!feature || !model) return Promise.resolve();

  const prompt = toInt(promptTokens);
  const completion = toInt(completionTokens);
  const total = toInt(totalTokens) || prompt + completion;

  const url = `${API_BASE_URL}doctor/ai-usage`;
  const payload = {
    feature,
    model: String(model).slice(0, 64),
    prompt_tokens: prompt,
    completion_tokens: completion,
    total_tokens: total,
    duration_seconds: toInt(durationSeconds),
    appointment_id: appointmentId != null ? Number(appointmentId) || null : null,
  };

  return axios
    .post(url, payload, {
      headers: { "Content-Type": "application/json", ...getAuthHeader() },
      timeout: 15000,
    })
    .then(() => undefined)
    .catch((err) => {
      // Best-effort telemetry: log loudly for debugging but never rethrow.
      console.warn(
        `[ai-usage] report failed (${err?.response?.status ?? "no response"}) POST ${url}`,
        err?.response?.data ?? err?.message ?? err,
        payload,
      );
    });
}
